import {
  Component,
  OnInit,
  Inject,
  PLATFORM_ID,
  OnDestroy,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { IUserSelectEmail } from '../../core/interfaces/user-select-email';
import { UserSelectEmailService } from '../../core/services/user-select-email.service';
import { constantTxt } from '../../core/constants/constants';
import { NzTableModule } from 'ng-zorro-antd/table';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-user-select-email',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        MatSnackBarModule,
        NzTableModule,
    ],
    templateUrl: './user-select-email.component.html',
    styleUrls: ['./user-select-email.component.less']
})
export class UserSelectEmailComponent implements OnInit, OnDestroy {
  groupEmail: string = '';
  form: FormGroup;
  items: IUserSelectEmail[] = [];
  isHovered: boolean[] = [];
  showPopup = false;
  isLoading = false;
  errorMessage = '';
  isBrowser: boolean = false;
  constant = constantTxt;
  selectedGroupEmail: string = '';
  getAllListSubscription!: Subscription;

  constructor(
    private readonly fb: FormBuilder,
    private readonly userSelectEmailService: UserSelectEmailService,
    private readonly snackBar: MatSnackBar,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.form = this.fb.group({
      HdItemAmount: [''],
      DoAction: [''],
      groupEmail: [''],
    });
  }

  ngOnInit(): void {
    if (!this.isBrowser) {
      return; // Skip initialization on server-side
    }

    // Load any previously selected group email from localStorage
    const storedGroupEmail = localStorage.getItem('groupEmail');
    if (storedGroupEmail) {
      this.selectedGroupEmail = storedGroupEmail;
    }

    this.groupEmail = '';
    this.items = [];
    this.isHovered = [];
  }

  loadItems(): void {
    if (!this.isBrowser) {
      return; // Skip HTTP calls on server-side
    }

    if (!this.groupEmail || this.groupEmail.trim() === '') {
      this.snackBar.open('Please enter a group name to search.', 'Close', {
        duration: 3000,
      });
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const filters = { cond: this.groupEmail || '' };
    this.getAllListSubscription = this.userSelectEmailService
      .getAllList(filters)
      .subscribe({
        next: (response) => {
          this.isLoading = false;

          // Extract data from HttpResponse
          const data = response.body;

          let parsedData: any = data;
          let itemsArray: any[] = [];

          // Handle different response formats
          if (typeof data === 'string') {
            // Try to parse as JSON
            try {
              parsedData = JSON.parse(data);
            } catch (error) {
              this.items = [];
              this.isHovered = [];
              this.errorMessage = 'Invalid JSON format received from server';
              return;
            }
          } else if (data === null || data === undefined) {
            this.items = [];
            this.isHovered = [];
            this.errorMessage = '';
            return;
          } else {
            parsedData = data;
          }

          // Handle both array and single object responses
          if (Array.isArray(parsedData)) {
            itemsArray = parsedData;
          } else if (parsedData && typeof parsedData === 'object') {
            // Single object response - convert to array
            itemsArray = [parsedData];
          } else if (
            parsedData === null ||
            parsedData === undefined ||
            parsedData === ''
          ) {
            this.items = [];
            this.isHovered = [];
            this.errorMessage = '';
            return;
          } else {
            this.items = [];
            this.isHovered = [];
            this.errorMessage = 'Invalid response format from server';
            return;
          }

          // Handle DataTable JSON structure - extract rows
          if (itemsArray.length > 0 && typeof itemsArray[0] === 'object') {
            // Check if this looks like a DataTable structure
            const firstItem = itemsArray[0];
            if ('group_name' in firstItem && 'group_mail' in firstItem) {
              // Direct object structure - use as is
            } else {
              // Might be DataTable structure - try to extract actual data
              let extractedData: any[] = [];

              // Try different extraction methods for DataTable
              if (firstItem.Rows && Array.isArray(firstItem.Rows)) {
                extractedData = firstItem.Rows;
              } else if (firstItem.rows && Array.isArray(firstItem.rows)) {
                extractedData = firstItem.rows;
              } else if (Array.isArray(firstItem)) {
                extractedData = firstItem;
              } else {
                // Try to find any array property that contains our data
                for (const key of Object.keys(firstItem)) {
                  const value = firstItem[key];
                  if (Array.isArray(value) && value.length > 0) {
                    const sampleItem = value[0];
                    if (
                      sampleItem &&
                      typeof sampleItem === 'object' &&
                      ('group_name' in sampleItem || 'group_mail' in sampleItem)
                    ) {
                      extractedData = value;
                      break;
                    }
                  }
                }
              }

              if (extractedData.length > 0) {
                itemsArray = extractedData;
              }
            }
          }

          // Validate items have required properties
          const validationResults = itemsArray.map((item) => {
            const isObject = item && typeof item === 'object';
            const hasGroupName = 'group_name' in item;
            const hasGroupMail = 'group_mail' in item;

            return isObject && hasGroupName && hasGroupMail;
          });

          const isValidData = validationResults.every((result) => result);

          if (isValidData && itemsArray.length > 0) {
            this.items = itemsArray;
            this.isHovered = new Array(this.items.length).fill(false);
            this.errorMessage = ''; // Clear any previous error
          } else if (itemsArray.length === 0) {
            this.items = [];
            this.isHovered = [];
            this.errorMessage = ''; // Don't show error for empty results
          } else {
            this.items = [];
            this.isHovered = [];
            this.errorMessage = 'Invalid data structure received from server';
          }
        },
        error: (error) => {
          this.isLoading = false;

          let errorMsg = 'Error loading email data';
          if (error.status === 500) {
            errorMsg = 'Server error - please check the backend service';
          } else if (error.status === 404) {
            errorMsg = 'Email service endpoint not found';
          } else if (error.status === 0) {
            errorMsg =
              'Cannot connect to server - please check your connection';
          } else if (error.error?.message) {
            errorMsg = `Error: ${error.error.message}`;
          }

          this.errorMessage = errorMsg;
          this.items = [];
          this.isHovered = [];
          this.snackBar.open(errorMsg, 'Close', { duration: 5000 });
        },
      });
  }

  onMouseOver(index?: number): void {
    if (index !== undefined) {
      this.isHovered[index] = true;
    }
  }

  onMouseOut(index: number): void {
    this.isHovered[index] = false;
  }

  onRadioButtonChange(selectedItem: IUserSelectEmail): void {
    this.selectedGroupEmail = selectedItem.group_mail || '';
    localStorage.setItem('groupEmail', selectedItem.group_mail || '');
    window.close();
  }

  goAddEmail(): void {
    const selectedEmails: string[] = [];
    const inputs = document.getElementsByTagName('input');

    for (let i = 0; i < inputs.length; i++) {
      if (inputs[i].type === 'checkbox' && inputs[i].checked) {
        selectedEmails.push(inputs[i].value);
      }
    }

    if (selectedEmails.length < 1) {
      this.snackBar.open('You did not select any Email.', 'Close', {
        duration: 3000,
      });
      return;
    }

    this.selectedGroupEmail = selectedEmails[0];
    localStorage.setItem('groupEmail', selectedEmails[0]);

    this.snackBar.open('Email selected successfully!', 'Close', {
      duration: 3000,
    });

    window.close();
  }

  checkQA(code: string): boolean {
    return this.selectedGroupEmail === code;
  }

  trackByIndex(index: number): number {
    return index;
  }

  ngOnDestroy(): void {
    this.getAllListSubscription?.unsubscribe();
  }
}
